/**
 * W98 (V6.0) — Heute-Inbox: „Muss ich heute handeln — und wenn ja, was?"
 *
 * Zwei pure Builder:
 *  1. buildMarktChips()  — die Markt-Ampel-Leiste (6 Chips), ersetzt
 *     inhaltlich 8 Markt-Karten als Erstinformation. Deskriptiv: die Chips
 *     zeigen den MARKT-ZUSTAND (Stress = rot), nicht die Kaufempfehlung —
 *     die antizyklische Übersetzung („Panik = Kaufzone") machen die
 *     Kandidaten-Ampel (ampel.ts) und die Entscheidungs-Karten.
 *  2. buildInboxCards()  — max. 3 Entscheidungs-Karten aus den DB-first-
 *     Quellen des 18:15-Crons + Positions-Zustand. Kein Ereignis = keine
 *     Karte; „Heute nichts zu tun" ist ein Feature, kein Fehler.
 */

import type { AmpelTone } from '@/lib/ampel';

// ---------------------------------------------------------------------------
// 1) Markt-Ampel-Leiste
// ---------------------------------------------------------------------------

export interface MarktChip {
  key: 'phase' | 'makro' | 'sentiment' | 'vix' | 'kapitulation' | 'krypto';
  label: string;
  /** Kurzwert im Chip, z.B. "Korrektur", "VIX 31", "54/100". */
  value: string;
  tone: AmpelTone;
  /** 1 Satz fürs Tooltip/Drilldown. */
  hint: string;
}

export interface MarktChipsInput {
  phaseAggregate: 'bull' | 'korrektur' | 'bear' | 'accumulation' | 'neutral' | null;
  vix: number | null;
  macroRegime: 'risk_on' | 'neutral' | 'risk_off' | 'crisis' | null;
  sentimentSignal: 'bullish_contra' | 'neutral' | 'bearish_contra' | null;
  sentimentFearGreed: number | null;
  capitulationScore: number | null;
  capitulationFlag: 'none' | 'elevated' | 'capitulation' | null;
  thrustState: 'none' | 'setup' | 'thrust_fired' | null;
  cryptoSupportive: boolean | null;
}

const PHASE_LABEL: Record<string, string> = {
  bull: 'Aufwärts',
  korrektur: 'Korrektur',
  bear: 'Bärenmarkt',
  accumulation: 'Bodenbildung',
  neutral: 'Seitwärts',
};

export function buildMarktChips(input: MarktChipsInput): MarktChip[] {
  const chips: MarktChip[] = [];

  // Phase (SPY-Trend + Vol + Rotation aggregiert)
  const agg = input.phaseAggregate;
  chips.push({
    key: 'phase',
    label: 'Phase',
    value: agg ? PHASE_LABEL[agg] ?? agg : '—',
    tone:
      agg === 'bull' || agg === 'accumulation'
        ? 'green'
        : agg === 'bear'
          ? 'red'
          : agg
            ? 'yellow'
            : 'gray',
    hint:
      agg === 'accumulation'
        ? 'Markt bildet nach dem Abverkauf einen Boden — Umfeld für erste Tranchen.'
        : agg === 'bear'
          ? 'Übergeordneter Abwärtstrend — Trend-Käufe haben Gegenwind.'
          : agg === 'korrektur'
            ? 'Markt korrigiert — Beobachtungsphase, Einkaufsliste schärfen.'
            : agg === 'bull'
              ? 'Markt im Aufwärtstrend.'
              : 'Marktphase aktuell nicht ermittelbar.',
  });

  // Makro (FRED: Zinskurve, HY-Spreads, Fed)
  const m = input.macroRegime;
  chips.push({
    key: 'makro',
    label: 'Makro',
    value:
      m === 'risk_on'
        ? 'Risk-on'
        : m === 'risk_off'
          ? 'Risk-off'
          : m === 'crisis'
            ? 'Krise'
            : m
              ? 'Neutral'
              : '—',
    tone:
      m === 'risk_on' ? 'green' : m === 'neutral' ? 'yellow' : m ? 'red' : 'gray',
    hint:
      m === 'crisis'
        ? 'Makro-Stress (Spreads/Zinskurve) — Positionsgrößen klein halten.'
        : m === 'risk_off'
          ? 'Defensives Makro-Umfeld — Qualität bevorzugen.'
          : m === 'risk_on'
            ? 'Unterstützendes Makro-Umfeld.'
            : 'Makro ohne klares Signal.',
  });

  // Sentiment (Kontra-Indikator: Angst = grün für den Antizykliker)
  const s = input.sentimentSignal;
  const fg = input.sentimentFearGreed;
  chips.push({
    key: 'sentiment',
    label: 'Stimmung',
    value: fg !== null ? `F&G ${Math.round(fg)}` : s ? s.replace('_contra', '') : '—',
    tone:
      s === 'bullish_contra' ? 'green' : s === 'bearish_contra' ? 'red' : s ? 'yellow' : 'gray',
    hint:
      s === 'bullish_contra'
        ? 'Breite Angst — als Kontra-Signal GUT für antizyklische Käufe.'
        : s === 'bearish_contra'
          ? 'Breite Gier — als Kontra-Signal Vorsicht bei neuen Käufen.'
          : 'Stimmung neutral.',
  });

  // VIX (Stress-Niveau)
  const vix = input.vix;
  chips.push({
    key: 'vix',
    label: 'VIX',
    value: vix !== null ? `${Math.round(vix)}` : '—',
    tone: vix === null ? 'gray' : vix < 20 ? 'green' : vix < 28 ? 'yellow' : 'red',
    hint:
      vix === null
        ? 'Kein VIX-Wert.'
        : vix >= 28
          ? `VIX ${Math.round(vix)} — Panik-Niveau: kleine Größen, aber Augen auf für Kapitulations-Käufe.`
          : vix >= 20
            ? `VIX ${Math.round(vix)} — erhöhte Schwankung, Größe anpassen.`
            : `VIX ${Math.round(vix)} — ruhiger Markt.`,
  });

  // Kapitulation (eigener Komposit aus Breite/VIX-TS/Thrust)
  const cap = input.capitulationScore;
  const flag = input.capitulationFlag;
  chips.push({
    key: 'kapitulation',
    label: 'Kapitulation',
    value: cap !== null ? `${Math.round(cap)}/100` : '—',
    tone:
      input.thrustState === 'thrust_fired'
        ? 'green'
        : flag === 'capitulation'
          ? 'red'
          : flag === 'elevated'
            ? 'yellow'
            : cap !== null
              ? 'green'
              : 'gray',
    hint:
      input.thrustState === 'thrust_fired'
        ? 'Breiten-Thrust gezündet — historisch starkes Boden-Bestätigungs-Signal.'
        : flag === 'capitulation'
          ? 'Klimaktischer Abverkauf — Stress maximal, antizyklisches Fenster beachten.'
          : flag === 'elevated'
            ? 'Erhöhter Markt-Stress — Limits vorbereiten, nicht hinterherverkaufen.'
            : 'Kein klimaktischer Stress.',
  });

  // Krypto-Regime
  const cs = input.cryptoSupportive;
  chips.push({
    key: 'krypto',
    label: 'Krypto',
    value: cs === null ? '—' : cs ? 'Aufwärts' : 'Abwärts',
    tone: cs === null ? 'gray' : cs ? 'green' : 'red',
    hint:
      cs === null
        ? 'Kein Krypto-Regime ermittelbar.'
        : cs
          ? 'BTC über der 200-Tage-Linie — Trend unterstützt.'
          : 'BTC unter der 200-Tage-Linie — Boden-Leiter zählt, nicht der Trend.',
  });

  return chips;
}

// ---------------------------------------------------------------------------
// 2) Entscheidungs-Karten (max 3)
// ---------------------------------------------------------------------------

export interface InboxDecisionCard {
  key: string;
  tone: AmpelTone;
  emoji: string;
  /** Kurztitel, z.B. "NVDA: Stop in Gefahr". */
  title: string;
  /** Ein Satz mit Zahlen — was ist los? */
  satz: string;
  /** Imperative Handlung, z.B. "Position prüfen". */
  aktion: string;
  /** Router-Ziel. */
  route: string;
}

export interface InboxInput {
  /** Positionen ≤5 % über Stop (Dashboard-stopRiskPositions). */
  stopRisk: Array<{ symbol: string; distancePct: number | null; stop: number }>;
  // `planTriggers` ist am 11.08.2026 entfallen. Die Karte „Plan im
  // Trigger-Bereich" verlinkte auf /plans — eine Route, die mit PR 7
  // gelöscht wurde; der Klick führte ins Leere. Erzeugt werden konnten
  // Trade-Pläne seit PR 8 ohnehin nicht mehr, die Karte zeigte also nur noch
  // Altbestand aus der abgeschalteten Engine. Was zu kaufen ist, steht in
  // „Was ist zu tun" und in den offenen Kaufzonen — aus der gemessenen Regel.
  /** Krypto-Böden mit offenem Kauf-Fenster. */
  bodenReady: Array<{ symbol: string; bodenScore: number }>;
  /** Stage-4-Böden mit Reversal-Zeichen (stabilizing). */
  stabilizing: Array<{ symbol: string; layer: string; score: number }>;
  /** Markt-Kapitulation (für die Markt-Chance-Karte). */
  capitulationFlag: 'none' | 'elevated' | 'capitulation' | null;
  thrustState: 'none' | 'setup' | 'thrust_fired' | null;
}

export interface InboxResult {
  cards: InboxDecisionCard[];
  /** Wieviele Ereignisse über das 3er-Limit hinaus anstehen. */
  overflow: number;
}

export const INBOX_MAX_CARDS = 3;

export function buildInboxCards(input: InboxInput): InboxResult {
  const all: InboxDecisionCard[] = [];

  // Priorität 1 — Verteidigung: Position nahe am Stop (Geld in Gefahr).
  for (const p of input.stopRisk) {
    const pct = p.distancePct !== null ? (p.distancePct * 100).toFixed(1) : '?';
    all.push({
      key: `stop:${p.symbol}`,
      tone: 'red',
      emoji: '🛑',
      title: `${p.symbol}: nur ${pct} % über dem Stop`,
      satz: `Der Kurs steht ${pct} % über deinem Stop (${p.stop.toFixed(2)}). Entweder der Plan hält — oder er wird heute ausgeführt.`,
      aktion: 'Position ansehen',
      route: '/positions',
    });
  }

  // Priorität 2 — Kauf-Fenster Krypto-Boden (mechanische Tranche 1).
  for (const b of input.bodenReady) {
    all.push({
      key: `boden:${b.symbol}`,
      tone: 'green',
      emoji: '🟢',
      title: `${b.symbol}: Kauf-Fenster offen (Tranche 1)`,
      satz: `Boden-Score ${Math.round(b.bodenScore)}/100 — Tranche 1 als Limit an der 200-Wochen-Linie.`,
      // Beschriftung am 11.08.2026 von „Trade-Plan öffnen" auf das Ziel
      // gezogen: Es gibt keine Trade-Pläne mehr, der Link führte schon vorher
      // auf die Symbolseite.
      aktion: 'Symbolseite öffnen',
      route: `/symbol/crypto/${encodeURIComponent(b.symbol)}`,
    });
  }

  // Priorität 3 — Reversal-Zeichen auf beobachteten Böden.
  for (const r of input.stabilizing) {
    all.push({
      key: `radar:${r.symbol}`,
      tone: 'yellow',
      emoji: '🔻',
      title: `${r.symbol}: erstes Wende-Zeichen`,
      satz: `Stage-4-Boden stabilisiert sich (Boden-Wert ${Math.round(r.score)}) — Zeit, die Tranchen-Limits zu prüfen.`,
      aktion: 'Kandidat ansehen',
      route: `/symbol/${r.layer}/${encodeURIComponent(r.symbol)}`,
    });
  }

  // Priorität 4 — Markt-Ereignis als Karte (nur die starken Signale).
  if (input.thrustState === 'thrust_fired') {
    all.push({
      key: 'markt:thrust',
      tone: 'green',
      emoji: '🚀',
      title: 'Markt: Breiten-Thrust gezündet',
      satz: 'Die Marktbreite ist binnen weniger Tage von ausgewaschen auf stark gedreht — historisch eines der besten Boden-Bestätigungs-Signale.',
      aktion: 'Kapitulations-Radar ansehen',
      // Audit 13.07.: '#'-Route = auf der Heute-Seite Markt-Details öffnen (der
      // Radar liegt dort), statt auf sich selbst zu navigieren.
      route: '#markt-details',
    });
  } else if (input.capitulationFlag === 'capitulation') {
    all.push({
      key: 'markt:kapitulation',
      tone: 'red',
      emoji: '🌊',
      title: 'Markt: klimaktischer Abverkauf',
      satz: 'Kapitulations-Signale aktiv — nicht hinterherverkaufen; vorbereitete Limits sind jetzt die Einkaufsliste.',
      aktion: 'Kapitulations-Radar ansehen',
      // Audit 13.07.: '#'-Route = auf der Heute-Seite Markt-Details öffnen (der
      // Radar liegt dort), statt auf sich selbst zu navigieren.
      route: '#markt-details',
    });
  }

  return {
    cards: all.slice(0, INBOX_MAX_CARDS),
    overflow: Math.max(0, all.length - INBOX_MAX_CARDS),
  };
}
